import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { scrollToPageTop } from '../../../../../utils/functions';
import type { LotteryDrawResult } from '../../../types';
import { prizeList2goodsList } from '../../../utils';
import { canAutoplayVideo } from '../utils/canAutoplayVideo';

// シーン1(演出動画)からシーン2へ移すきっかけは、動画側のイベントを主とし、時計に頼るタイマーは保険に回す。
// 動画のダウンロードや再生開始が遅れると、マウント基準のタイマーでは演出が途中で打ち切られるため。
// 検知手段は以下の4つで、最初に成立した1つだけでシーン遷移する(leaveScene1 は多重実行しない)。
//   1. ended        … 再生が終わった(あたり・虹の通常経路)
//   2. timeupdate   … ハズレのみ。終了の少し手前で遷移させる(下記 MISS_TRANSITION_LEAD_SECONDS)
//   3. ボタン押下   … 動画が再生できない(error)ときだけ出す「抽選結果を見る」ボタン
//   4. タイムアウト … 上記のいずれも起きない場合の保険(下記 SCENE1_FALLBACK_TIMEOUT_MS)
//
// error は 3 のボタンを出すきっかけであり、それ自体はシーン遷移させない。かつては error 専用に
// 動画の尺に合わせたタイマー(ハズレ3600ms / 通常あたり5000ms / 確変演出11000ms)を張っていたが、
// 廃止した。動画が再生できない状況では映像が1フレームも出ないため「動画の尺のあいだ待たせる」
// 根拠がなく、ハズレの3600msも「ホワイトアウト中に遷移を始めて白画面を抑える」という
// 映像ありきの根拠だった。現在は error の有無にかかわらず、ユーザーの操作か 4 のタイムアウトで抜ける。

// 動画のイベントが一切来ないまま画面が止まるのを防ぐための上限時間(ミリ秒)。
// この時間を過ぎたら ended を待たずにシーン遷移する。
// 動画が再生できない場合もこの値で抜ける（ユーザーがボタンを押さなかった場合）。
const SCENE1_FALLBACK_TIMEOUT_MS = 30000;

// ハズレで、動画終了の何秒前にシーン遷移を始めるか。
// ハズレはシーン遷移がページ遷移(/nothing)になるため、演出のホワイトアウト中に遷移を始めて
// 白画面が表示され続ける時間を抑えている。時計ではなく再生位置を基準にするため読み込みの遅れに影響されない。
const MISS_TRANSITION_LEAD_SECONDS = 1.4;

// 動画のダウンロード完了を判定する際に、duration と buffered の末尾の差として許容する秒数。
// 厳密比較(buffered の末尾 >= duration)にしないのは、両者がブラウザーによってわずかにずれるため。
// 実測では WebKit が buffered の末尾を duration より 0.07 秒大きく、Firefox は逆に duration を
// buffered の末尾より 0.07 秒大きく報告した。**Firefox では厳密比較が永久に成立しない。**
const PRELOAD_BUFFERED_TOLERANCE_SECONDS = 0.25;

// 上記の判定が成立しない環境向けの保険。再生位置が終了までこの秒数を切ったら先読みを始める。
// ここまで再生が進んでいれば、その分のダウンロードは終わっている。
const PRELOAD_REMAINING_SECONDS = 2;

export function useLotteryDrawAnimeV2(
  lotteryDrawResult: LotteryDrawResult,
  // シーン2で使う CSS 背景画像の URL を stagingRank ごとに返す関数。
  // どのファイルを指すかは variant によって変わるため、呼び出し元(コンポーネント)から受け取る。
  getScene2ImageUrls: (stagingRank: string) => string[],
) {
  const router = useRouter();
  // 初期状態は detecting（自動再生可否の判定中）。マウント時に判定し、可能なら scene 1（動画再生）へ
  // 直接遷移して transfer シーンを省略する。不可なら transfer シーンを表示し、ユーザー操作（ボタン押下）を
  // 起点に scene 1 へ遷移させて自動再生制約を回避する。scene 1・2 の遷移・挙動は演出開始後も従来どおり。
  const [scene, setScene] = useState<'detecting' | 'transfer' | 1 | 2>('detecting');
  const [currentGoodsIndex, setCurrentGoodsIndex] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');

  // prizeList2goodsList の処理を内包
  const goodsList = prizeList2goodsList(lotteryDrawResult.prize);

  // goodsListがない=ハズレ=特殊値入れておく
  const maxRank = goodsList.length > 0 ? goodsList.map((v) => v.stagingRank).sort()[0] : '-1';

  // 昇格区分
  const SPECIAL_TYPE_SILVER_RAINBOW = 1;
  const SPECIAL_TYPE_GOLD_RAINBOW = 2;
  const SPECIAL_TYPE_BLUE_RAINBOW = 3;

  // 虹(maxRank === '1')のときに再生する確変演出の種類を決定する。
  // 虹を引いたら必ず確変演出を再生するため、ここでは「どの確変演出を出すか」だけを決める。
  // 種類は表向きランダムにしている(33%の確率で銀から虹に昇格、33%の確率で金から虹に昇格、残りの確率で青から虹に昇格)
  const getSpecialType = () => {
    const random = Math.random();
    if (random < 0.33) return SPECIAL_TYPE_SILVER_RAINBOW;
    if (random < 0.66) return SPECIAL_TYPE_GOLD_RAINBOW;
    return SPECIAL_TYPE_BLUE_RAINBOW;
  };

  // 確変演出の種類に対応する動画のパスを返す
  const getSpecialVideoUrl = (specialTypeValue: number) => {
    if (specialTypeValue === SPECIAL_TYPE_SILVER_RAINBOW) {
      // 銀から虹に昇格させる動画
      return '/anime/sp/v2/animation_special_intro_silver_rainbow.mp4';
    }
    if (specialTypeValue === SPECIAL_TYPE_GOLD_RAINBOW) {
      // 金から虹に昇格させる動画
      return '/anime/sp/v2/animation_special_intro_gold_rainbow.mp4';
    }
    // 青から虹に昇格させる動画
    return '/anime/sp/v2/animation_special_intro_blue_rainbow.mp4';
  };

  // NOTE: シーン2のフェードインは SCSS の CSS アニメーションが担い、本フックには関与するコードがない。
  //   以前は anime.js のタイムラインで景品画像・賞名・下部情報(ボタン含む)をフェードインさせていたが、
  //   CSS の opacity: 0 を起点にタイムラインでしか表示されない構造だったため、タイムラインが走らないと
  //   ボタンが透明のまま残り操作できなくなっていた（play() がオプショナルチェーンで無音失敗するため
  //   復帰手段もない）。いったん全廃したのち、景品画像と景品名だけを CSS アニメーションで戻している。
  //   **件数表示と「次へ」「SKIP」にはフェードインをかけない。** さらに CSS 側は基準値を opacity: 1 の
  //   ままにしてキーフレームを 0 → 1 にしているため、アニメーションが走らなくても要素は見えたままになる。
  //   「次へ」での再生し直しは LotteryDrawAnimeV2.tsx の key が担う（下記 nextGoods 参照）。
  //   集中線の回転は CSS アニメーション(.imageFlash の rotate-anime)で、いずれとも無関係に動き続ける。

  // 動画のURL設定
  // 複数回実行されると都合が悪いため、初回マウント時の1回だけ実行する
  const isVideoUrlSetRef = useRef(false);
  useEffect(() => {
    if (isVideoUrlSetRef.current) return;
    isVideoUrlSetRef.current = true;

    // 虹(最上位ランク)を引いた場合は必ず確変演出を再生する。種類は確率で決定する。
    if (maxRank === '1') {
      setVideoUrl(getSpecialVideoUrl(getSpecialType()));
      return;
    }

    // 虹以外(あたり・ハズレ)は通常の動画
    setVideoUrl('/anime/sp/v2/animation_normal_intro.mp4');
  }, [goodsList.length, maxRank]);

  // 自動再生可否を判定し、初期シーンを確定する。マウント時の1回だけ実行する。
  // 可能なら transfer シーンを省略して scene 1（動画再生）から開始、不可なら transfer シーンを表示する。
  useEffect(() => {
    let cancelled = false;
    canAutoplayVideo().then((canAutoplay) => {
      if (cancelled) return;
      setScene(canAutoplay ? 1 : 'transfer');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // scene が切り替わったらページのスクロール位置を最上部へ戻す。
  // 演出ページ（.pageAnime）は 100vh・min-height 600px で組まれており、可視領域がそれより低い環境
  // （iOS Safari は 100vh がツールバーを含まない高さで解決されるため常に該当。ほかに可視領域 600px 未満の
  // 端末・ウインドウ）ではページ側にスクロールの余りが生まれる。各シーンは position: absolute; top: 0 で
  // ページ先頭に貼り付くため、前のシーンで生じたスクロール量が残ると、シーンの上端が見切れた状態で
  // 初期表示されてしまう。シーン遷移は状態遷移（URL 遷移なし）のため、ブラウザーや Next.js の
  // スクロールリセットは働かず、自前で戻す必要がある。
  //
  // 依存は scene のみにする。scene 2 内の「次へ」（currentGoodsIndex の更新）では走らせない。
  // 景品を送る操作でユーザーが見ている位置を奪わないため、依存に currentGoodsIndex を加えないこと。
  //
  // スクロールは「effect 内で即時」と「requestAnimationFrame の 1 フレーム後」の 2 回実行する。
  //   即時 … requestAnimationFrame だけに任せると、シーン切替の次フレームまで前のシーンの
  //          スクロール量が残ることを実測で確認したため、effect が走った時点で先に戻しておく。
  //          useEffect はコミット後（描画後になることもある）に走るため描画前は保証されないが、
  //          ずれた状態が見えるフレームは減らせる。描画前を保証する useLayoutEffect は
  //          サーバーレンダリングで警告が出るため採らない
  //   1 フレーム後 … レイアウトが確定してから念のためもう一度戻す。慣性スクロールの最中に
  //          シーンが切り替わった場合など、即時実行だけでは戻り切らないケースの保険
  //          （OptionBar.tsx の先行実装が requestAnimationFrame を使うのと同じ理由）
  useEffect(() => {
    scrollToPageTop();
    const rafId = window.requestAnimationFrame(() => {
      scrollToPageTop();
    });
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [scene]);

  // 動画が再生できない状態かどうか（error イベントを受け取ったか）。
  // 「抽選結果を見る」ボタンと、再生に失敗した旨の案内文言を出す判断に使う。
  const [isVideoUnplayable, setIsVideoUnplayable] = useState(false);

  // 動画の再生が始まったかどうか。開始までは画面が黒（PC）・明るい灰色（SP）のまま変化がなく
  // フリーズしたように見えるため、開始までローディング表示を出す判断に使う。
  // 動画が再生できない場合・ダウンロードが完了しない場合は false のままとなり、表示を出し続ける。
  const [hasVideoStarted, setHasVideoStarted] = useState(false);

  // シーン1から抜ける処理。複数の検知手段（ended / timeupdate / 保険タイムアウト）と
  // 「抽選結果を見る」ボタンの押下から呼ばれるため、最初の1回だけ実行する。
  // error は本処理を呼ばない（シーン遷移の契機ではなく「抽選結果を見る」ボタンを出す契機であり、
  // そこから先はユーザーの押下か保険タイムアウトで抜ける）。
  // あたりはシーン2へ状態遷移し、ハズレは /nothing へページ遷移する。
  //
  // ハズレだけ結果画面(/result)ではなく /nothing へ分岐させているのは、結果画面が景品の存在を
  // 前提にしているため。LotteryResult は複数口引き(multiDrawId あり)のとき
  // prize[0].goods[0].cutoffId をオプショナルチェーンなしで参照しており、景品ゼロだと
  // 例外になる（単発引きは参照が ?. のため落ちないが景品ゼロの結果画面が表示される）。
  //
  // 先頭のガードは、ボタン押下の直後に保険タイマーや動画のイベント（ended / timeupdate）が
  // 発火しても二重に遷移させないためのもの。遷移でアンマウントされればタイマーは cleanup で
  // 止まるが、それを待たずに閉じる。
  const hasLeftScene1Ref = useRef(false);
  const leaveScene1 = useCallback(() => {
    if (hasLeftScene1Ref.current) return;
    hasLeftScene1Ref.current = true;

    if (goodsList.length === 0) {
      // ハズレ
      router.replace(`/draw/${lotteryDrawResult.historyId}/nothing`);
      return;
    }
    // あたり（通常）
    setScene(2);
  }, [goodsList.length, lotteryDrawResult.historyId, router]);

  // 動画の再生が終わったらシーン遷移する（あたり・虹の通常経路）。
  // 読み込みや再生開始が遅れても、演出を途中で打ち切らずに最後まで見せられる。
  const handleVideoEnded = () => {
    leaveScene1();
  };

  // シーン2で使う CSS 背景画像（背景・集中線・等賞バッジ）を先読みする。
  //
  // これらは SCSS の background-image で指定しているため、シーン2が display: none の間は
  // ブラウザーが取得しない。切り替わってから読み始めるぶん表示が遅れ、景品画像や景品名
  // （どちらも <img> やテキストなので待機中に読み込みが終わっている）だけが先に出る。
  // 同じ URL をここで取得してキャッシュに載せておくと、切り替え時にネットワークへ行かずに済む。
  //
  // **開始は演出動画のダウンロードが終わってから**にすること（下記 shouldStartPreload）。
  // 早く始めると動画と帯域を奪い合う。実測では、読み込み直後に始めると再生開始が 4.7〜7.7 秒
  // 遅れ、再生開始後に始めると再生が 2 回・3.5〜7.6 秒途切れた。ダウンロード完了後なら
  // 動画の挙動（再生開始・途切れの回数と時間）は先読みなしと完全に一致する。
  //
  // 先読みが間に合わないままシーン2へ遷移しても壊れない。CSS 側の要求は進行中のリクエストを
  // 共有するため二重取得にならず、先読みなしと同じ挙動に劣化するだけ（Chromium / WebKit /
  // Firefox で確認済み）。そのため失敗時の処理は持たない。
  //
  // 先読みが**失敗**した場合も同じく、先読みなしと結果が変わらないことを3エンジンで実測した
  // （素材が無い / 一時的な 5xx / 通信断 / 壊れたデータ）。取得が失敗として確定していれば、
  // CSS 側は自力で取り直す。ただし共有する以上は失敗も共有するため、**先読みが進行中のまま
  // シーン2へ切り替わり、そのあとに通信が切れると背景が出ないまま**になる。これは先読み特有の
  // 悪化ではなく、CSS の背景画像が一度失敗すると自動では再取得されないという性質による
  // （先読みなしの対照でも同じ結果になった）。
  //
  // なお、この仕組みは配信がキャッシュ可能であることが前提。素材は /_next/static/ 配下に
  // 置かれ `public, max-age=31536000, immutable` で配信されるため成立する。
  // **キャッシュ制御を変えると先読みが黙って無効になる**（表示は壊れないが効果が消える）。
  // 逆に**エラー応答へ明示的な Cache-Control を付けてもいけない**。Chromium は先読みが受け取った
  // エラーを覚え込み、配信が復旧してもシーン2の要求をキャッシュから解決するため背景が出なくなる。
  // 現行の配信（dev は Cache-Control なしの 403、本番は Cache-Control なしの 404）では
  // 3エンジンとも取り直すことを実測で確認済み。
  const hasPreloadedRef = useRef(false);
  const preloadScene2Images = () => {
    if (hasPreloadedRef.current) return;
    hasPreloadedRef.current = true;

    // 実際に表示されるランクの素材だけを対象にする
    // （複数口引きでランクが複数あれば、その数だけ先読みする）
    const stagingRanks = Array.from(new Set(goodsList.map((goods) => goods.stagingRank)));
    // 集中線はランクに依らず同じ1枚のため、ランクが複数あると同じ URL が並ぶ。重複を除いてから取得する。
    // 通信は増えない（同一 URL への取得を1本にまとめる挙動を Chromium / WebKit / Firefox で確認済み）が、
    // 無駄な Image を作らずに済む。
    const urls = Array.from(new Set(stagingRanks.flatMap(getScene2ImageUrls)));
    for (const url of urls) {
      const image = new Image();
      image.src = url;
    }
  };

  // 先読みを始めてよいか（＝動画のダウンロードが終わったか）を判定する。
  //
  // どちらの条件も満たさないまま保険タイムアウト（SCENE1_FALLBACK_TIMEOUT_MS）でシーン2へ
  // 抜ける場合は、**先読みを走らせない**。ダウンロードが終わっていない＝動画がまだ帯域を
  // 使っている状態であり、そこで先読みを始めると動画の再生をさらに悪化させるため。
  // この経路はシーン2へ入ってから素材を読み始めることになり、先読みなしと同じ挙動になる。
  // 動画が再生できない場合（error）は帯域が空いているため、下の handleVideoError で即座に始める。
  const shouldStartPreload = (video: HTMLVideoElement) => {
    if (!Number.isFinite(video.duration)) return false;
    if (video.buffered.length > 0) {
      const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      if (bufferedEnd >= video.duration - PRELOAD_BUFFERED_TOLERANCE_SECONDS) return true;
    }
    // buffered での判定が成立しない環境の保険
    return video.duration - video.currentTime <= PRELOAD_REMAINING_SECONDS;
  };

  // ハズレのみ、動画終了の少し手前でシーン遷移を始める（MISS_TRANSITION_LEAD_SECONDS）。
  // あたりは ended を待つため、シーン遷移はここでは行わず、シーン2の素材の先読みだけを行う。
  const handleVideoTimeUpdate = (video: HTMLVideoElement) => {
    if (goodsList.length > 0) {
      // あたり。シーン2へ進むため、動画のダウンロードが終わった時点で素材を先読みする
      if (shouldStartPreload(video)) preloadScene2Images();
      return;
    }
    // 以下はハズレのみ。シーン2を表示しないため先読みの対象外。
    // メタデータ未取得やストリーミングでは duration が NaN / Infinity になるため、その場合は ended に任せる
    if (!Number.isFinite(video.duration)) return;
    if (video.duration - video.currentTime > MISS_TRANSITION_LEAD_SECONDS) return;
    leaveScene1();
  };

  // 動画が再生できない場合は ended も timeupdate も来ないため、案内文言を切り替えて
  // 「抽選結果を見る」ボタンを表示し、ユーザーが自分で先へ進めるようにする。
  // ボタンを押さなかった場合は保険タイムアウトで抜ける。
  const handleVideoError = () => {
    setIsVideoUnplayable(true);
    // 動画が落ちてこないため帯域が空いている。ダウンロードの完了を待つ理由がないので先読みを始める。
    // この経路では timeupdate が来ないため、ここで呼ばないと先読みが一度も走らない。
    // ハズレでも呼ばれるが、goodsList が空のため先読み対象の URL が 0 件になり何も起きない。
    preloadScene2Images();
  };

  // 実際に再生が始まった時点でローディング表示を消す。
  // 描画の有無ではなく再生開始の状態で判定するため、動画の透過挙動やレイヤー順に依存しない。
  const handleVideoPlaying = () => {
    setHasVideoStarted(true);
  };

  // 動画のイベントが来ないまま画面が止まるのを防ぐ保険。シーン1の開始から一定時間で強制的に遷移する。
  // 動画が再生できない場合（error）もこのタイマーで抜ける。以前は error 専用に動画の尺へ合わせた
  // 短いタイマーを別に張っていたが、映像が出ない状況で尺ぶん待たせる根拠がないため廃止した。
  // ユーザーには「抽選結果を見る」ボタンを出しているため、待たずに進むこともできる。
  useEffect(() => {
    // 判定中（detecting）・演出開始前（transfer）は動画を再生していないためタイマーを張らない
    if (scene !== 1) return;
    const timer = setTimeout(leaveScene1, SCENE1_FALLBACK_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [scene, leaveScene1]);

  // ブラウザバック抑制（修正版）
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const browserBackBlockFunc = () => {
      history.go(1);
    };

    window.history.pushState(null, '', location.href);
    window.addEventListener('popstate', browserBackBlockFunc);

    return () => {
      window.removeEventListener('popstate', browserBackBlockFunc);
    };
  }, []);

  // 現在の商品取得
  const currentGoods = () => {
    return goodsList[currentGoodsIndex];
  };

  // 結果画面への遷移
  const goResult = () => {
    router.replace(`/draw/${lotteryDrawResult.historyId}/result`);
  };

  // 次の商品への遷移
  const nextGoods = () => {
    if (currentGoodsIndex + 1 === goodsList.length) {
      // 結果画面へ
      router.replace(`/draw/${lotteryDrawResult.historyId}/result`);
    } else {
      // 次の景品を表示する。景品画像と景品名のフェードインは、LotteryDrawAnimeV2.tsx が
      // currentGoodsIndex を含む key で要素を作り直すことによって再生し直される
      setCurrentGoodsIndex((prev) => prev + 1);
    }
  };

  // transfer シーンから演出（scene 1 の動画再生）を開始する。
  // ユーザー操作（ボタン押下）を起点にすることで video の自動再生制約を回避する。
  const handleStartAnimation = () => {
    setScene(1);
  };

  return {
    scene,
    currentGoodsIndex,
    videoUrl,
    goodsList,
    maxRank,
    currentGoods,
    goResult,
    leaveScene1,
    nextGoods,
    handleStartAnimation,
    handleVideoEnded,
    handleVideoTimeUpdate,
    handleVideoError,
    handleVideoPlaying,
    hasVideoStarted,
    isVideoUnplayable,
  };
}
